import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../supabaseClient";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";
import HeaderBar from "../components/HeaderBar";
import CoordinateFinder from "../components/CoordinateFinder";
import PriceInput from "../components/PriceInput";

const PlaceEdit = () => {
  const { placeId } = useParams();
  const [place, setPlace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categoryList, setCategoryList] = useState([]);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const isNew = !placeId;

  useEffect(() => {
    const init = async () => {
      // 🔹 카테고리 목록 불러오기
      const { data: categories, error: catError } = await supabase
        .from("places_category")
        .select("*")
        .order("num", { ascending: true });

      if (!catError) setCategoryList(categories);

      if (isNew) {
        // 🔹 신규 등록 초기 상태
        setPlace({
          title: "",
          address: "",
          use: true,
          link: "",
          price: "",
          age_min: "",
          age_max: "",
          is_reservation: false,
          is_parking: false,
          lat: "",
          lng: "",
          category_id: "",
        });
        setLoading(false);
        return;
      }

      // 🔹 기존 장소 데이터 불러오기
      const { data: placeData, error: placeError } = await supabase
        .from("places")
        .select("*")
        .eq("id", placeId)
        .single();

      const { data: locData } = await supabase
        .from("places_locations")
        .select("lat, lng")
        .eq("place_id", placeId)
        .maybeSingle();

      if (placeError) {
        alert("장소 정보를 불러올 수 없습니다.");
        return;
      }

      setPlace({
        ...placeData,
        lat: locData?.lat || "",
        lng: locData?.lng || "",
        age_min: placeData.age_min || "",
        age_max: placeData.age_max || "",
        use: placeData.use ?? true,
        link: placeData.link || "",
        price: placeData.price ? JSON.stringify(placeData.price) : "",
        is_reservation: placeData.is_reservation ?? false,
        is_parking: placeData.is_parking ?? false,
        category_id: placeData.category_id || "",
      });

      setLoading(false);
    };

    init();
  }, [placeId, isNew]);

  const handleUpdate = async () => {
    const { lat, lng } = place;
    let parsedPrice = null;

    try {
      const temp = place.price ? JSON.parse(place.price) : null;
      if (temp && typeof temp === "object" && Object.values(temp).every((v) => Number(v) === 0)) {
        parsedPrice = null;
      } else {
        parsedPrice = temp;
      }
    } catch (e) {
      console.warn("가격 형식 오류:", e);
      parsedPrice = null;
    }

    let currentPlaceId = placeId;

    if (isNew) {
      const { data, error } = await supabase
        .from("places")
        .insert({
          title: place.title,
          address: place.address,
          use: place.use,
          link: place.link,
          price: parsedPrice,
          is_reservation: place.is_reservation,
          is_parking: place.is_parking,
          category_id: place.category_id ? parseInt(place.category_id) : null,
        })
        .select()
        .single();

      if (error) {
        showToast("장소 등록 실패");
        return;
      }

      currentPlaceId = data.id;
    } else {
      const { error } = await supabase
        .from("places")
        .update({
          title: place.title,
          address: place.address,
          use: place.use,
          link: place.link,
          price: parsedPrice,
          age_min: place.age_min ? parseInt(place.age_min) : null,
          age_max: place.age_max ? parseInt(place.age_max) : null,
          is_reservation: place.is_reservation,
          is_parking: place.is_parking,
          category_id: place.category_id ? parseInt(place.category_id) : null,
        })
        .eq("id", placeId);

      if (error) {
        showToast("업데이트 실패");
        return;
      }
    }

    const { error: locError } = await supabase
      .from("places_locations")
      .upsert(
        {
          place_id: currentPlaceId,
          lat: parseFloat(lat),
          lng: parseFloat(lng),
          location: `SRID=4326;POINT(${lng} ${lat})`,
        },
        { onConflict: "place_id" }
      );

    if (locError) {
      showToast("위치 저장 실패");
    } else {
      showToast(isNew ? "등록 완료!" : "저장 완료!");
    }
  };

  const showToast = (message) => {
    setToastMessage(message);
    setToastOpen(true);
  };
  
  if (loading || !place) return <Typography>로딩 중...</Typography>;

  return (
    <Container sx={{ pb: 7, pt: 7, pl: 0, pr: 0, display: "flex", flexDirection: "column" }}>
      <HeaderBar title={isNew ? "장소 등록" : "장소 수정"} showBack />
      <Box sx={{ pl: 2, pr: 2, pt: 4, flex: 1 }}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>카테고리</InputLabel>
          <Select
            value={place.category_id}
            onChange={(e) => setPlace({ ...place, category_id: e.target.value })}
            label="카테고리"
          >
            {categoryList.map((cat) => (
              <MenuItem key={cat.id} value={cat.id}>
                {cat.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Checkbox
              checked={place.use}
              onChange={(e) => setPlace({ ...place, use: e.target.checked })}
            />
          }
          label="사용 여부"
        />

        <TextField
          label="외부 링크"
          value={place.link}
          onChange={(e) => setPlace({ ...place, link: e.target.value })}
          fullWidth
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={place.is_reservation}
              onChange={(e) => setPlace({ ...place, is_reservation: e.target.checked })}
            />
          }
          label="예약 가능"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={place.is_parking}
              onChange={(e) => setPlace({ ...place, is_parking: e.target.checked })}
            />
          }
          label="주차 가능"
        />

        <TextField
          label="장소 이름"
          value={place.title}
          onChange={(e) => setPlace({ ...place, title: e.target.value })}
          fullWidth
          sx={{ mt: 2, mb: 2 }}
        />
        <Box sx={{ display: "flex", gap: 2, mb:1 }}>
          <TextField
            label="이용 가능 최소 나이"
            type="number"
            value={place.age_min}
            onChange={(e) => setPlace({ ...place, age_min: e.target.value })}
            fullWidth
          />
          <TextField
            label="이용 가능 최대 나이"
            type="number"
            value={place.age_max}
            onChange={(e) => setPlace({ ...place, age_max: e.target.value })}
            fullWidth
          />
        </Box>
        <PriceInput
          value={place.price}
          onChange={(newPriceJson) => setPlace({ ...place, price: newPriceJson })}
        />

        <TextField
          label="주소"
          value={place.address}
          onChange={(e) => setPlace({ ...place, address: e.target.value })}
          fullWidth
          sx={{ mt: 3, mb: 1 }}
        />

        <Divider sx={{ my: 2 }} />

        <CoordinateFinder
          address={place.address}
          onSetCoordinates={(coord) => {
            setPlace({
              ...place,
              lat: coord.lat,
              lng: coord.lng,
              location: coord.location,
            });
          }}
        />

        <TextField
          label="위도 (lat)"
          value={place.lat}
          onChange={(e) => setPlace({ ...place, lat: e.target.value })}
          fullWidth
          sx={{ mt: 2 }}
        />
        <TextField
          label="경도 (lng)"
          value={place.lng}
          onChange={(e) => setPlace({ ...place, lng: e.target.value })}
          fullWidth
          sx={{ mt: 2, mb: 3 }}
        />

        <Button variant="contained" onClick={handleUpdate} fullWidth>
          {isNew ? "등록하기" : "저장하기"}
        </Button>
      </Box>

      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={() => setToastOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </MuiAlert>
      </Snackbar>

    </Container>
  );
};

export default PlaceEdit;
